import { useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HelpCircle } from "lucide-react";

const TOOLTIP_WIDTH = 260;
const GAP = 8;

/**
 * `?` アイコンにホバー/フォーカスで説明を出すヘルプツールチップ。
 *
 * 吹き出しは createPortal で body 直下に fixed 配置する。トリガー基準の絶対配置だと
 * モーダル/ドロワーの overflow(hidden/auto) にクリップされて見えなくなるため
 * (旧実装の不具合)、親の overflow に一切影響されないポータル方式にしている。
 * 画面右端に収まらない場合はトリガーの左側に出す。
 */
export function HelpTooltip({ text, label }: { text: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipId = useId();

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // 既定は右側。右に収まらなければ左に出す。
    const fitsRight = window.innerWidth - r.right >= TOOLTIP_WIDTH + GAP + 8;
    const left = fitsRight ? r.right + GAP : r.left - TOOLTIP_WIDTH - GAP;
    setPos({ top: r.top + r.height / 2, left });
  }, [open]);

  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label ? `${label}のヘルプ` : `ヘルプ: ${text}`}
        aria-describedby={open ? tooltipId : undefined}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 18,
          height: 18,
          border: 0,
          borderRadius: 3,
          background: "transparent",
          color: "var(--ink-3)",
          padding: 0,
          cursor: "help",
        }}
      >
        <HelpCircle size={14} aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <span
            id={tooltipId}
            role="tooltip"
            style={{
              position: "fixed",
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              transform: "translateY(-50%)",
              zIndex: 1000,
              width: TOOLTIP_WIDTH,
              padding: "6px 8px",
              border: "0.5px solid var(--rule)",
              borderRadius: 4,
              background: "var(--bg-raised)",
              color: "var(--ink)",
              boxShadow: "0 8px 24px rgba(0,0,0,.14)",
              fontSize: 11,
              lineHeight: 1.5,
              // 位置確定前はちらつき防止で不可視 (a11y ツリーには残すため opacity を使う)。
              opacity: pos ? 1 : 0,
              transition: "opacity .12s ease",
              pointerEvents: "none",
            }}
          >
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}
